import { useEffect, useState } from "react";
import {
  isEmptyPayoutPayload,
  isPayoutApiMode,
  shouldFallbackPayoutReads,
} from "./payoutApiClient";

export type PayoutResourceState<T> = {
  data: T;
  loading: boolean;
  error: string | null;
  usingFallback: boolean;
  source: "mock" | "api";
  refresh: () => void;
};

type Loader<T> = (signal: AbortSignal) => Promise<T>;

const emptyApiData = <T,>(mockData: T): T => {
  if (Array.isArray(mockData)) return [] as T;
  if (mockData && typeof mockData === "object") {
    return Object.fromEntries(
      Object.entries(mockData as Record<string, unknown>).map(([key, value]) => {
        if (Array.isArray(value)) return [key, []];
        if (value && typeof value === "object") return [key, emptyApiData(value)];
        if (typeof value === "number") return [key, 0];
        if (typeof value === "boolean") return [key, false];
        if (typeof value === "string") return [key, ""];
        return [key, value];
      }),
    ) as T;
  }
  return mockData;
};

export function usePayoutResource<T>(
  mockData: T,
  apiLoader: Loader<T>,
  deps: unknown[] = [],
): PayoutResourceState<T> {
  const [data, setData] = useState<T>(() =>
    isPayoutApiMode() ? emptyApiData(mockData) : mockData,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(!isPayoutApiMode());
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (!isPayoutApiMode()) {
      setData(mockData);
      setError(null);
      setUsingFallback(true);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    apiLoader(controller.signal)
      .then((result) => {
        if (isEmptyPayoutPayload(result) && shouldFallbackPayoutReads()) {
          setData(mockData);
          setUsingFallback(true);
          return;
        }
        setData(result);
        setUsingFallback(false);
      })
      .catch((apiError: unknown) => {
        if (controller.signal.aborted) return;
        const message =
          apiError instanceof Error
            ? apiError.message
            : "Unable to load payouts API data.";
        setError(message);
        if (shouldFallbackPayoutReads()) {
          setData(mockData);
          setUsingFallback(true);
          return;
        }
        // API mode without mock fallback must never retain demo seed data.
        setData(emptyApiData(mockData));
        setUsingFallback(false);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken, ...deps]);

  return {
    data,
    loading,
    error,
    usingFallback,
    source: isPayoutApiMode() && !usingFallback ? "api" : "mock",
    refresh: () => setRefreshToken((current) => current + 1),
  };
}

export const usePayoutMutationGuard = () => {
  const apiMode = isPayoutApiMode();

  return (usingFallback = false) => {
    if (!apiMode) {
      return {
        allowed: false,
        reason: "This action is running in mock mode. Switch REACT_APP_PAYOUTS_DATA_SOURCE=api to submit it.",
      };
    }
    if (usingFallback) {
      return {
        allowed: false,
        reason: "Live payouts API data is unavailable, so money-moving actions are blocked instead of using demo data.",
      };
    }
    return { allowed: true, reason: "" };
  };
};
